import { useEffect, useRef, useState } from 'react';
import {
  getMostPopularCourseAPI,
  getTimeSpentUserDataAPI,
  getUserActivityDataAPI,
  getUserPathwaysAndResourcesTotalAPI,
  getUserSkillLeaderBoardAPI,
  getUserTotalCredentialsAPI,
} from './utils/profileActivity';
import {
  getUserEarnedSkillsAPI,
  getUserHighlightedSkillsAPI,
  getUserSelfReportedSkillsAPI,
} from './utils/profileSkills';
import { getUserCoursesAPI } from './utils/profileCourses';
import { getUserProgramsAPI } from './utils/profilePrograms';

export function useProfileActivity(username: string, userId: number) {
  const hasFetchedData = useRef<boolean>(false);

  const [timeSpentGraphData, setTimeSpentGraphData] = useState([]);
  const [timeSpentGraphDataLoaded, setTimeSpentGraphDataLoaded] =
    useState(false);
  const [userActivityData, setUserActivityData] = useState([]);
  const [userActivityDataLoaded, setUserActivityDataLoaded] = useState(false);
  const [mostPopularCourseData, setMostPopularCourseData] = useState([]);
  const [mostPopularCourseDataLoaded, setMostPopularCourseDataLoaded] =
    useState(false);
  const [userSkillLeaderBoardData, setUserSkillLeaderBoardData] = useState([]);
  const [userSkillLeaderBoardDataLoaded, setUserSkillLeaderBoardDataLoaded] =
    useState(false);
  const [userProgramsTotal, setUserProgramsTotal] = useState('');
  const [userProgramsTotalLoaded, setUserProgramsTotalLoaded] = useState(false);
  const [userCredentialsTotal, setUserCredentialsTotal] = useState('');
  const [userCredentialsTotalLoaded, setUserCredentialsTotalLoaded] =
    useState(false);
  const [userPathwaysAndResourcesTotal, setUserPathwaysAndResourcesTotal] =
    useState('');
  const [
    userPathwaysAndResourcesTotalLoaded,
    setUserPathwaysAndResourcesTotalLoaded,
  ] = useState(false);
  const [userCoursesTotal, setUserCoursesTotal] = useState(0);
  const [userCoursesTotalLoaded, setUserCoursesTotalLoaded] = useState(false);
  const [totalUserSkills, setTotalUserSkills] = useState(0);
  const [totalUserSkillsLoaded, setTotalUserSkillsLoaded] = useState(false);
  const [userCourses, setUserCourses] = useState([])
  const [userCoursesLoaded , setUserCoursesLoaded] = useState(false)

  useEffect(() => {
    if (!hasFetchedData.current) {
      getTimeSpentUserDataAPI(username, timeSpentGraphCallback);
      getUserActivityDataAPI(username, userActivityCallback);
      getMostPopularCourseAPI(username, mostPopularCourseCallback);
      getUserSkillLeaderBoardAPI(username, userSkillLeaderBoardCallback);
      getUserTotalCredentialsAPI(username, userCredentialsTotalCallback);
      getUserPathwaysAndResourcesTotalAPI(
        username,
        userPathwaysAndResourcesTotalCallback
      );
      getUserCoursesAPI(username).then((courses) => {
        userCoursesTotalCallback(courses);
      });

      getUserProgramsAPI(username, (programs) => {
        userProgramsTotalCallback(programs?.length);
      });

      const fetchData = async () => {
        try {
          // Fetch data from the first endpoint
          const earnedSkillsPromise: Promise<any[]> = new Promise((resolve) => {
            getUserEarnedSkillsAPI(username, (data) => resolve(data));
          });

          // Fetch data from the second endpoint
          const highlightedSkillsPromise: Promise<any[]> = new Promise(
            (resolve) => {
              getUserHighlightedSkillsAPI(username, (data) => resolve(data));
            }
          );

          // Fetch data from the third endpoint
          const selfReportedSkillsPromise: Promise<any[]> = new Promise(
            (resolve) => {
              getUserSelfReportedSkillsAPI(username, (data) => resolve(data));
            }
          );

          // Wait for all promises to resolve
          const [earnedSkills, highlighted, selfReported] = await Promise.all([
            earnedSkillsPromise,
            highlightedSkillsPromise,
            selfReportedSkillsPromise,
          ]);

          // Update the state with the aggregated data
          setTotalUserSkills(
            (earnedSkills?.length || 0) +
              (highlighted?.length || 0) +
              (selfReported?.length || 0)
          );
          setTotalUserSkillsLoaded(true);
        } catch (error) {
          console.error('Error fetching skills:', error);
        }
      };
      fetchData();

      hasFetchedData.current = true;
    }
  }, [username, userId]);

  const userProgramsTotalCallback = (data) => {
    setUserProgramsTotal(data);
    setUserProgramsTotalLoaded(true);
  };
  const userCredentialsTotalCallback = (data) => {
    setUserCredentialsTotal(data);
    setUserCredentialsTotalLoaded(true);
  };
  const userPathwaysAndResourcesTotalCallback = (data) => {
    setUserPathwaysAndResourcesTotal(data);
    setUserPathwaysAndResourcesTotalLoaded(true);
  };
  const userCoursesTotalCallback = (data) => {
    setUserCourses(data)
    setUserCoursesLoaded(true)
    setUserCoursesTotal(data.length);
    setUserCoursesTotalLoaded(true);
  };

  const timeSpentGraphCallback = (data) => {
    setTimeSpentGraphData(data);
    setTimeSpentGraphDataLoaded(true);
  };
  const userActivityCallback = (data) => {
    setUserActivityData(data);
    setUserActivityDataLoaded(true);
  };
  const mostPopularCourseCallback = (data) => {
    setMostPopularCourseData(data);
    setMostPopularCourseDataLoaded(true);
  };
  const userSkillLeaderBoardCallback = (data) => {
    setUserSkillLeaderBoardData(data);
    setUserSkillLeaderBoardDataLoaded(true);
  };

  return [
    timeSpentGraphData as any,
    timeSpentGraphDataLoaded as any,
    userActivityData as any,
    userActivityDataLoaded as any,
    mostPopularCourseData as any,
    mostPopularCourseDataLoaded as any,
    userSkillLeaderBoardData as any,
    userSkillLeaderBoardDataLoaded as any,
    userProgramsTotal as any,
    userProgramsTotalLoaded as boolean,
    userCredentialsTotal as any,
    userCredentialsTotalLoaded as boolean,
    userPathwaysAndResourcesTotal as any,
    userPathwaysAndResourcesTotalLoaded as boolean,
    userCoursesTotal as any,
    userCoursesTotalLoaded as boolean,
    totalUserSkills as any,
    totalUserSkillsLoaded as any,
    userCourses as any,
    userCoursesLoaded as boolean
  ];
}

export default useProfileActivity;
